import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useVirtualizer, type Virtualizer } from '@tanstack/react-virtual';
import {
  LOAD_MORE_THRESHOLD_PX,
  STICK_TO_BOTTOM_PX,
  TIMELINE_ESTIMATED_ROW_PX,
  TIMELINE_OVERSCAN,
} from '../constants';
import type { TimelineRow } from '../types';
import { useTimelineAnchor } from './useTimelineAnchor';

interface Params {
  rows: readonly TimelineRow[];
  hasOlder: boolean;
  isFetchingOlder: boolean;
  loadOlder: () => void;
  /**
   * Permalink target. While it is set the list parks on that row instead of
   * sticking to the newest message — otherwise opening a link to an old
   * message would scroll straight past it to the tail.
   */
  anchorId?: string | null;
}

interface Result {
  scrollRef: (node: HTMLDivElement | null) => void;
  virtualizer: Virtualizer<HTMLDivElement, Element>;
  atBottom: boolean;
  scrollToBottom: (behavior?: ScrollBehavior) => void;
}

/**
 * Windowed message list. Only the visible rows plus a small overscan exist in
 * the DOM, so a 50 000-message conversation costs the same as a 50-message one.
 * Also owns the two behaviours virtualisation makes hard: staying pinned to
 * the newest message, and keeping the viewport steady when older pages prepend.
 */
export function useVirtualTimeline({
  rows,
  hasOlder,
  isFetchingOlder,
  loadOlder,
  anchorId = null,
}: Params): Result {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const [atBottom, setAtBottom] = useState(anchorId === null);
  /* The same fact as `atBottom`, readable from a ResizeObserver callback. */
  const pinned = useRef(anchorId === null);
  const anchor = useRef<number | null>(null);
  const previousCount = useRef(rows.length);
  /* Has this timeline ever been placed at the bottom? See the layout effect. */
  const landed = useRef(false);

  const virtualizer = useVirtualizer<HTMLDivElement, Element>({
    count: rows.length,
    getScrollElement: () => element,
    estimateSize: (index) => rows[index]?.sizeHint ?? TIMELINE_ESTIMATED_ROW_PX,
    getItemKey: (index) => rows[index]?.id ?? index,
    overscan: TIMELINE_OVERSCAN,
  });

  const scrollToBottom = useCallback(
    (behavior: ScrollBehavior = 'auto') => {
      if (!element) return;
      element.scrollTo({ top: element.scrollHeight, behavior });
    },
    [element],
  );

  const onScroll = useCallback(() => {
    if (!element) return;
    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    pinned.current = distanceToBottom < STICK_TO_BOTTOM_PX;
    setAtBottom(pinned.current);
    if (element.scrollTop < LOAD_MORE_THRESHOLD_PX && hasOlder && !isFetchingOlder) {
      anchor.current = element.scrollHeight - element.scrollTop;
      loadOlder();
    }
  }, [element, hasOlder, isFetchingOlder, loadOlder]);

  useEffect(() => {
    if (!element) return undefined;
    element.addEventListener('scroll', onScroll, { passive: true });
    return () => element.removeEventListener('scroll', onScroll);
  }, [element, onScroll]);

  /* Prepending older pages must not move the reading position. */
  useLayoutEffect(() => {
    if (!element) return;

    /*
     * OPEN AT THE NEWEST MESSAGE.
     *
     * Everything below only reacts to the list GROWING, and `previousCount` is
     * seeded with `rows.length` AT MOUNT. Reopening a conversation is the
     * common case and its messages are already in the query cache, so the
     * first render already has every row: nothing grows, no branch runs, and
     * the list stays at scrollTop 0 — the oldest message, with the newest one
     * below the fold. That is the "message is cut off, I have to scroll down"
     * report, and it happened on every revisit while a cold load happened to
     * look fine.
     *
     * `anchorId` is the deliberate exception: arriving from a permalink or a
     * search result means somebody asked for a SPECIFIC message, and
     * `useTimelineAnchor` is what puts them there.
     */
    if (!landed.current && rows.length > 0) {
      landed.current = true;
      if (anchorId === null) {
        element.scrollTop = element.scrollHeight;
        return;
      }
    }

    const grew = rows.length > previousCount.current;
    previousCount.current = rows.length;
    if (!grew) return;
    if (anchor.current !== null) {
      element.scrollTop = element.scrollHeight - anchor.current;
      anchor.current = null;
    } else if (atBottom) {
      element.scrollTop = element.scrollHeight;
    }
  }, [anchorId, atBottom, element, rows.length]);

  /*
   * STAY ON THE NEWEST MESSAGE WHILE THE ROWS SETTLE.
   *
   * Landing puts the list at the bottom of the ESTIMATED height. The rows then
   * measure - a file card, a voice clip, an image, a link preview are all
   * taller than the estimate - and the real bottom moved below the fold: a chat
   * opened one screen short of its newest message with "2 new messages"
   * showing. The composer growing by a line did the same. While the reader is
   * at the bottom, any change in height keeps them there; scrolling up is what
   * lets go.
   */
  const totalSize = virtualizer.getTotalSize();
  useLayoutEffect(() => {
    if (element && landed.current && pinned.current) element.scrollTop = element.scrollHeight;
  }, [element, totalSize]);

  useEffect(() => {
    if (!element || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      if (landed.current && pinned.current) element.scrollTop = element.scrollHeight;
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [element]);

  useTimelineAnchor({ rows, virtualizer, anchorId, ready: element !== null });

  return { scrollRef: setElement, virtualizer, atBottom, scrollToBottom };
}
